import React, { useState } from 'react';
import { Modal, Box, Typography, Button, CircularProgress } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

interface NoAccessPageProps {
  open: boolean;
  onClose: () => void;
}

const style = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 1,
  p: 4,
  minWidth: 360,
  maxWidth: '90vw',
  border: '2px solid #ff3d00',
  zIndex: 10000,
};

const NoAccessPage: React.FC<NoAccessPageProps> = ({ open, onClose }) => {
  const [retrying, setRetrying] = useState(false);

  const handleTryAgain = async () => {
    setRetrying(true);
    try {
      // Import lazily to avoid circular deps at module load time
      const { msalInstance, backendApiRequest } = await import('../config/msal');
      const activeAccount = msalInstance.getActiveAccount();

      // Force a new interactive login so Azure AD issues a fresh token that
      // includes any App Role assignments granted since last sign-in.
      await msalInstance.acquireTokenRedirect({
        ...backendApiRequest,
        prompt: 'select_account',
        ...(activeAccount ? { account: activeAccount } : {}),
      });
      // acquireTokenRedirect navigates away; execution stops here.
    } catch {
      // If the redirect call itself fails, fall back to a hard reload which
      // will re-trigger the MSAL login redirect in App.tsx.
      window.location.href = '/';
    }
  };

  return (
    <Modal
      open={open}
      aria-labelledby="no-access-title"
      disableEscapeKeyDown
      disableAutoFocus
      disableEnforceFocus
      disablePortal={true}
      disableRestoreFocus={true}
      hideBackdrop={false}
      keepMounted={true}
      style={{ zIndex: 9999 }}
    >
      <Box sx={style}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ErrorOutlineIcon sx={{ color: '#ff3d00', fontSize: 32, mr: 1 }} />
          <Typography id="no-access-title" variant="h5" fontWeight={700} color="#ff3d00">
            Access Denied
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          You don't have access to use Bolt.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please contact your administrator to request access to the application.
          Once access has been granted, sign in again to continue.
        </Typography>
        <Button
          variant="outlined"
          startIcon={retrying ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={handleTryAgain}
          disabled={retrying}
          sx={{ borderColor: '#ff3d00', color: '#ff3d00', '&:hover': { borderColor: '#c62800', color: '#c62800' } }}
        >
          {retrying ? 'Redirecting…' : 'Try Again'}
        </Button>
      </Box>
    </Modal>
  );
};

export default NoAccessPage; 